import { useMutation, useQueryClient } from '@tanstack/react-query';

import type { MutationConfig } from '@/lib/react-query';
import type { StyleConfig, AdjustmentsConfig } from '../atoms/state';
import { getImage2VideoResultQueryOptions, startImage2Video } from './aiImageToVideo';

export type GenerateVideoPayload = {
  style: StyleConfig;
  adjustments?: AdjustmentsConfig;
};

export type GenerateVideoResponse = {
  jobId: string;
};

type GenerateVideoOptions = {
  mutationConfig?: MutationConfig<typeof generateVideo>;
};

export async function generateVideo(payload: GenerateVideoPayload): Promise<GenerateVideoResponse> {
  const images = payload.style.images;
  if (!images.length) {
    throw new Error('At least one image is required');
  }

  const startResponse = await startImage2Video({
    images: images.map((img) => img.url),
    durationSec: payload.style.durationSec,
    quality: payload.style.quality ?? '480p',
    cropToAspectRatio: payload.style.cropToAspectRatio,
  });

  return {
    jobId: startResponse.task_id,
  };
}

export const useGenerateVideo = ({ mutationConfig }: GenerateVideoOptions = {}) => {
  const queryClient = useQueryClient();

  return useMutation({
    mutationFn: generateVideo,
    ...mutationConfig,
    onSuccess: async (data, variables, context) => {
      if (data.jobId) {
        queryClient.prefetchQuery(getImage2VideoResultQueryOptions(data.jobId));
      }

      await mutationConfig?.onSuccess?.(data, variables, context);
    },
  });
};
